/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { nativeFetchRef } from '@amazon/vinyl-util'
import { IS_TAURI } from '@/util/platform'

// Tauri's WebView, like a browser, enforces CORS. Several backends Bullfrog
// talks to (notably Stratus on `music.amazon.com`) reject the app origin
// and break under WKWebView. The Tauri HTTP plugin proxies requests through
// Rust (reqwest), bypassing the WebView's CORS enforcement entirely.
// Override Vinyl's `nativeFetchRef` so every Vinyl requester goes through
// it. The override is no-op on web — leaving the browser's native `fetch`
// in place — so the web build's behavior is unchanged.
//
// Same-origin requests (i18n bundles, manifests, anything served by the
// `app://` URI scheme handler in Rust) skip the plugin and stay on the
// WebView's built-in fetch. reqwest only speaks http/https — routing
// `app://localhost/i18n/...` through the plugin fails with "scheme app
// not supported", which is what was breaking i18n on iOS.
//
// The plugin import is dynamic so the Tauri HTTP runtime ends up in its
// own lazily-loaded chunk and stays out of the web entry.

export async function initializeTauri() {
    if (!IS_TAURI) return
    const { fetch: pluginFetch } = await import('@tauri-apps/plugin-http')
    const builtinFetch = window.fetch.bind(window)
    nativeFetchRef.set(
        () => (input: RequestInfo | URL, init?: RequestInit) =>
            isSameOrigin(input) ? builtinFetch(input, init) : pluginFetch(input, init)
    )
}

function isSameOrigin(input: RequestInfo | URL): boolean {
    try {
        const url =
            typeof input === 'string'
                ? new URL(input, window.location.href)
                : input instanceof URL
                  ? input
                  : new URL(input.url, window.location.href)
        return url.origin === window.location.origin
    } catch {
        // An input that won't parse can't be routed through the plugin either —
        // reqwest only speaks http/https — so leave it on the WebView's fetch and
        // let that produce the real failure.
        return true
    }
}
